import type { CabinetResult, Issue, PanelPart, Vec3 } from './model.js';
import { TOLERANCE } from './units.js';
import { buildCabinet } from './cabinet.js';
import { EDGE_POLICIES, resolveEdges, type PartEdges } from './edgebanding.js';
import { buildAssembly } from './assembly.js';
import type { AssemblyInstance, CabinetInstance, ProjectDocument } from './document.js';
import { validateLinks } from './links.js';

interface EvaluatedBase {
  result: CabinetResult;
  edges: PartEdges[];
}

/** A placed unit: a parametric cabinet or a custom panel assembly. Both produce PanelParts. */
export type EvaluatedCabinet =
  | (EvaluatedBase & { kind: 'cabinet'; instance: CabinetInstance })
  | (EvaluatedBase & { kind: 'assembly'; instance: AssemblyInstance });

export interface EvaluatedProject {
  cabinets: EvaluatedCabinet[];
  issues: Issue[];
}

/**
 * Evaluates the whole document. Parts stay in cabinet-local coordinates; world placement is
 * applied only by viewers/exporters, so transforms never alter manufacturing dimensions.
 */
export function evaluateProject(doc: ProjectDocument): EvaluatedProject {
  const policy = EDGE_POLICIES[doc.settings.edgePolicy];
  const cabinets: EvaluatedCabinet[] = [];
  const issues: Issue[] = [];
  const seen = new Set<string>();
  const units: ({ kind: 'cabinet'; instance: CabinetInstance } | { kind: 'assembly'; instance: AssemblyInstance })[] = [
    ...doc.cabinets.map((instance) => ({ kind: 'cabinet' as const, instance })),
    ...(doc.assemblies ?? []).map((instance) => ({ kind: 'assembly' as const, instance })),
  ];
  for (const u of units) {
    const inst = u.instance;
    if (seen.has(inst.id)) {
      issues.push({ severity: 'error', code: 'doc.duplicate-id', message: `Dupli ID elementa ${inst.id}.`, cabinetId: inst.id });
      continue;
    }
    seen.add(inst.id);
    const result = u.kind === 'cabinet' ? buildCabinet(inst.id, u.instance.params) : buildAssembly(u.instance);
    if (inst.transform.mirrored) mirrorHinges(result.parts);
    const { edges, issues: edgeIssues } = resolveEdges(result.parts, policy, inst.edgeOverrides, inst.id, {
      environment: inst.environment ?? 'dry',
      bands: doc.settings.edgeBands,
    });
    issues.push(...result.issues, ...edgeIssues);
    cabinets.push({ ...u, result, edges } as EvaluatedCabinet);
  }
  issues.push(...detectCabinetOverlaps(cabinets));
  issues.push(...validateLinks(doc));
  return { cabinets, issues };
}

/**
 * World-space footprint of a cabinet (quarter turns only). Rotation matches the viewer:
 * Three.js rotation.y = −rotationY, i.e. x' = x·cosφ + z·sinφ, z' = −x·sinφ + z·cosφ with φ = −rotationY.
 */
export function cabinetWorldBox(c: EvaluatedCabinet): { min: Vec3; max: Vec3 } {
  const [sx, sy, sz] = c.result.bounds.size;
  const phi = (-c.instance.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const [px, py, pz] = c.instance.transform.position;
  const xs: number[] = [];
  const zs: number[] = [];
  for (const x of [0, sx]) {
    for (const z of [0, sz]) {
      xs.push(px + x * cos + z * sin);
      zs.push(pz - x * sin + z * cos);
    }
  }
  return { min: [Math.min(...xs), py, Math.min(...zs)], max: [Math.max(...xs), py + sy, Math.max(...zs)] };
}

function detectCabinetOverlaps(cabinets: EvaluatedCabinet[]): Issue[] {
  const issues: Issue[] = [];
  const visible = cabinets.filter((c) => !c.instance.hidden && c.result.parts.length > 0);
  const boxes = visible.map(cabinetWorldBox);
  for (let i = 0; i < visible.length; i++) {
    for (let j = i + 1; j < visible.length; j++) {
      const a = boxes[i]!;
      const b = boxes[j]!;
      const depth = Math.min(...[0, 1, 2].map((k) => Math.min(a.max[k]!, b.max[k]!) - Math.max(a.min[k]!, b.min[k]!)));
      if (depth > TOLERANCE.collision) {
        issues.push({
          severity: 'warning',
          code: 'scene.overlap',
          message: `Elementi ${visible[i]!.instance.name} i ${visible[j]!.instance.name} se preklapaju (${depth / 1000} mm).`,
          cabinetId: visible[i]!.instance.id,
          otherCabinetId: visible[j]!.instance.id,
        });
      }
    }
  }
  return issues;
}

/** A mirrored cabinet keeps all local dimensions and edge ids; only hinge sides swap. */
function mirrorHinges(parts: PanelPart[]): void {
  for (const p of parts) {
    if (p.movable === 'hinge-left') p.movable = 'hinge-right';
    else if (p.movable === 'hinge-right') p.movable = 'hinge-left';
  }
}
